import React, { useEffect, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import ConversationFilters from "./ConversationFilters";
import ConversationMetrics from "./ConversationMetrics";
import ConversationSessionHistory from "./ConversationSessionHistory";
import Conversation from "./Conversation";
import { ConversationAnalysisStyles } from "./ConversationAnalysisStyles";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  getAllConversationSessions,
  getConversationByFilter,
  getConversationBySession,
} from "./ConversatonAnalysisActions";
import { RootState } from "../../store/store";
import { IConversationFilters } from "../../models/admin/conversationAnalysis/conversationAnalysis";

const ConversationAnalysis: React.FC = () => {
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery("(max-width:767px)");

  const { conversationAnalysisData, conversations } = useAppSelector(
    (state: RootState) => state.conversationAnalysis
  );

  const [filters, setFilters] = useState<IConversationFilters>({
    fromDate: "",
    toDate: "",
  });

  useEffect(() => {
    dispatch(getAllConversationSessions());
  }, [dispatch]);

  const handleSelectSession = async (sessionId: string) => {
    if (sessionId) {
      await dispatch(getConversationBySession(sessionId));
    }
  };

  const handleDateChange = (type: "from" | "to", value: string) => {
    // If value is empty, reset the corresponding filter
    if (!value) {
      setFilters((prevFilters) => {
        const newFilters: IConversationFilters = {
          ...prevFilters,
          [type === "from" ? "fromDate" : "toDate"]: "",
        };
        dispatch(getConversationByFilter(newFilters));
        return newFilters;
      });
      return;
    }

    // Only proceed with date formatting if we have a valid value
    const date = new Date(value);
    const formattedDate = date.toISOString();

    setFilters((prevFilters) => {
      const newFilters: IConversationFilters = {
        ...prevFilters,
        [type === "from" ? "fromDate" : "toDate"]: formattedDate,
      };
      dispatch(getConversationByFilter(newFilters));
      return newFilters;
    });
  };

  return (
    <Box
      sx={{
        ...ConversationAnalysisStyles.container,
        maxWidth: isMobile ? "calc(100vw - 40px)" : "none",
      }}
      id="conversation_analysis_container"
      data-test-id="conversation_analysis_container"
      aria-label="Conversation Analysis"
    >
      <ConversationFilters
        filters={filters}
        handleDateChange={handleDateChange}
      />
      <ConversationMetrics />
      <Box
        sx={{
          ...ConversationAnalysisStyles.conversationsDataContainer,
          flexDirection: isMobile ? "column" : "row",
        }}
        id="conversation_data_container"
        data-test-id="conversation_data_container"
        aria-label="Conversation Data"
      >
        <ConversationSessionHistory
          sessionDetails={conversationAnalysisData || []}
          onSelectSession={handleSelectSession}
        />
        <Conversation
          selectedSession={
            conversations && Object.keys(conversations).length > 0
              ? conversations[Object.keys(conversations)[0]][0] || null
              : null
          }
        />
      </Box>
    </Box>
  );
};

export default ConversationAnalysis;
